
(function () {
  'use strict';

  // Base unit prices (cents) used to detect cart composition for the
  // sticky cart bar's per-unit copy. Update both numbers AND the live
  // base-product prices in Shopify admin in lockstep. Compare-at sales
  // are fine because item.original_price (the unitPrice variable below)
  // reads the *base* price, not the discounted line price.
  var WC_SHIRT_BASE_CENTS   = 3300;
  var WC_STICKER_BASE_CENTS = 399;

  // Idempotency guard: this script is referenced by multiple snippets
  // (cart-drawer, sticky-atc-bar, wc-buy-more-save-more, collection promo
  // strip). Browsers cache the file, but each <script> tag still executes
  // its IIFE — without this guard we'd double-subscribe to cartUpdate and
  // double-fetch /cart.js on every change.
  if (window.WCTier && window.WCTier.__inited) return;

  // Single source of truth — tier ladder math.
  var TIERS = [
    { qty: 1, off: 0,  per: 33.00, label: 'add 1 shirt'   },
    { qty: 2, off: 10, per: 29.70, label: 'mix 2 shirts'  },
    { qty: 3, off: 20, per: 26.40, label: 'mix 3 shirts'  },
    { qty: 4, off: 30, per: 23.10, label: 'mix 4 shirts'  },
    { qty: 5, off: 40, per: 19.80, label: 'mix 5 shirts'  },
    { qty: 6, off: 50, per: 16.50, label: 'mix 6+ shirts', top: true }
  ];

  var EVENT_NAME = 'wc:tier-updated';
  var lastState = null;

  // ---- math helpers ----
  function tierForCount(count) {
    if (count >= 6) return TIERS[5];
    if (count <= 0) return null;
    return TIERS[count - 1];
  }
  function nextTierForCount(count) {
    if (count >= 6) return null;
    if (count < 1) return TIERS[1]; // next milestone is 2-item / 10%
    return TIERS[count]; // count is 1..5 → index points at next tier
  }
  function isFreeGift(item) {
    // Match by VARIANT SKU — unique, unambiguous, won't collide with
    // any paid sticker SKU (the store sells hundreds of paid sticker
    // products at $3.99; price + title matching was too loose).
    // Existing B3G3F free shirts (price 0 + shirt SKU) should COUNT
    // toward the tier so previewing in the live B3G3F environment
    // doesn't confuse the bar — they don't match this SKU so they're
    // naturally excluded from the freebie set.
    //
    // EXTENSION PATH — if more freebies are added in the future
    // (e.g. "Free pin pack", "Free magnet w/ order"), promote this
    // to a SKU array and check membership. The matching Liquid logic
    // in snippets/cart-drawer.liquid + announcement-bar.liquid +
    // cart-icon-bubble.liquid + sections/header.liquid +
    // snippets/wc-sticky-cart-bar.liquid (the is_sticker_pack /
    // is_sticker assignments) MUST be updated in lockstep.
    return (item.sku || '') === 'WC-FreeMystery10pack-stickers';
  }

  // ---- core: fetch cart and broadcast ----
  function recompute() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = 0;
        // BMSM-savings base — sum of original_line_price (cents, pre-line
        // discounts) for non-gift items. Mirrors the Liquid logic in
        // snippets/cart-drawer.liquid so the JS-painted celebration
        // overlay savings matches the rendered cart drawer figure.
        var paidSubtotalCents = 0;
        var nonGiftItems = [];
        // Per-type quantities so the sticky cart-bar can pick the right
        // layout. Detection by base unit price (cents): 3300 = shirt,
        // 399 = sticker. Anything else (hoodies, sweatshirts, bumper
        // stickers, bumper magnets, pins) sets hasOther — the bar uses
        // a "% OFF + FREE GIFT" layout for those carts since their per-
        // unit prices vary too much to display cleanly.
        var shirtCount = 0;
        var stickerCount = 0;
        var hasOther = false;
        (cart.items || []).forEach(function (item) {
          if (isFreeGift(item)) return;
          count += item.quantity;
          var unitPrice = (typeof item.original_price === 'number')
            ? item.original_price
            : (item.price || 0);
          var lineRetail = (typeof item.original_line_price === 'number')
            ? item.original_line_price
            : unitPrice * item.quantity;
          paidSubtotalCents += lineRetail;
          nonGiftItems.push({ unit: unitPrice, qty: item.quantity });
          if (unitPrice === WC_SHIRT_BASE_CENTS)        shirtCount   += item.quantity;
          else if (unitPrice === WC_STICKER_BASE_CENTS) stickerCount += item.quantity;
          else                                          hasOther = true;
        });
        var tier = tierForCount(count);
        var next = nextTierForCount(count);
        var pct  = Math.min(100, (count / 6) * 100);
        var tierOff = tier ? tier.off : 0;

        // Per-item, per-unit floor savings — matches Shopify's
        // discount engine. Aggregate-then-divide overshoots by 1-2¢
        // on items whose base price doesn't divide evenly by the
        // tier % (e.g., $3.99 stickers at 50% off = 199¢ each, not
        // 200¢). Mirrors the same loop in cart-drawer.liquid.
        var bmsmSavingsCents = 0;
        if (tierOff > 0) {
          for (var i = 0; i < nonGiftItems.length; i++) {
            var unitSavings = Math.floor(nonGiftItems[i].unit * tierOff / 100);
            bmsmSavingsCents += unitSavings * nonGiftItems[i].qty;
          }
        }

        var state = {
          count: count,
          off: tierOff,
          per: tier ? tier.per : 33.00,
          tierIndex: tier ? TIERS.indexOf(tier) : -1,
          atTop: count >= 6,
          nextOff: next ? next.off : null,
          nextQty: next ? next.qty : null,
          nextPer: next ? next.per : null,
          pct: pct,
          cartTotal: cart.total_price || 0,
          paidSubtotalCents: paidSubtotalCents,
          bmsmSavingsCents: bmsmSavingsCents,
          shirtCount: shirtCount,
          stickerCount: stickerCount,
          hasOther: hasOther
        };
        lastState = state;

        document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: state }));
        return state;
      })
      .catch(function (err) {
        console.error('[wc-tier-controller] cart fetch error:', err);
      });
  }

  // Public API on window for snippets that want a synchronous read.
  window.WCTier = {
    TIERS: TIERS,
    EVENT_NAME: EVENT_NAME,
    getState: function () { return lastState; },
    recompute: recompute,
    tierForCount: tierForCount,
    nextTierForCount: nextTierForCount,
    __inited: true
  };

  // ---- wire up ----
  function init() {
    // Subscribe to Dawn's pubsub so cart changes anywhere refresh us.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { recompute(); });
    }
    // Catch external cart changes (UpCart leftovers, native cart page actions).
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') recompute();
    });
    // bfcache restore: when the user swipes back to a previously-visited
    // page, the page state is restored from cache — including the
    // server-rendered cart icon, which is now stale relative to actions
    // taken on the page they just left. Force a fresh recompute so all
    // tier-aware UI (header count badge, % OFF pill, ladder, etc.) repaints.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) recompute();
    });
    recompute();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();